<script setup lang="ts">
import { ProductInternalStatusesSelectDocument } from '~/graphql/operations'

const props = defineProps<{ statusId?: number | string | null }>()
const statusId = computed(() => Number(props.statusId) || null)
const { data, pause, resume } = useQuery({
  query: ProductInternalStatusesSelectDocument,
  variables: computed(() => ({ where: { id: { equals: statusId.value } }, perPage: 1 })),
  pause: true,
  requestPolicy: 'network-only',
})
// This urql version snapshots a computed pause option; control its pause ref explicitly.
watch(statusId, (value) => { if (value) resume(); else pause() }, { immediate: true, flush: 'sync' })
// Ignore an earlier response while the selected form value is being changed.
const selected = computed(() => statusId.value ? data.value?.productInternalStatusesSelect.data.find(
  (item) => item.id === statusId.value,
) : undefined)
const hovered = ref(false)
const focused = ref(false)
const pinned = ref(false)
const root = ref<HTMLElement>()
const tooltipId = useId()
const open = computed(() => !!selected.value && (hovered.value || focused.value || pinned.value))
const close = () => { hovered.value = false; focused.value = false; pinned.value = false }
watch(statusId, close)
onClickOutside(root, close)
</script>

<template>
  <span v-if="selected" ref="root" class="relative inline-flex ml-2 align-middle" @mouseenter="hovered = true" @mouseleave="hovered = false">
    <button
      type="button"
      aria-label="Internal Product Status information"
      :aria-describedby="open ? tooltipId : undefined"
      :aria-expanded="open"
      class="inline-flex text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-full"
      @focus="focused = true"
      @blur="focused = false"
      @click.stop.prevent="pinned = !pinned"
      @keydown.esc.stop.prevent="close"
    >
      <span class="i-heroicons-outline:information-circle w-4 h-4" aria-hidden="true" />
    </button>
    <span v-if="open" :id="tooltipId" role="tooltip" class="absolute bottom-full left-0 z-50 mb-2 w-72 max-w-[80vw] rounded-lg bg-white p-4 text-sm font-normal text-gray-900 shadow-lg ring-1 ring-black/10">
      <strong class="block mb-1">{{ selected.name }}</strong>
      <span class="block whitespace-pre-wrap break-words">{{ selected.description || 'No description provided.' }}</span>
    </span>
  </span>
</template>
